import React, { useState, useEffect, useMemo } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import ResourceDetailModal from './components/ResourceDetailModal';
import ResourceFilters from './components/ResourceFilters';
import AddResourceModal from './components/AddResourceModal';
import ResourceCalendarView from './components/ResourceCalendarView';
import ResourceGanttView from './components/ResourceGanttView';
import ResourceListView from './components/ResourceListView';
import BulkEditModal from './components/BulkEditModal';
import BulkAssignModal from './components/BulkAssignModal';
import ResourceConflictTab from './components/ResourceConflictTab';
import { resourceService } from '../../services/resourceService';
import { supabase } from '../../lib/supabase';
import { createRealtimeSubscription, RealtimeErrorType } from '../../utils/realtimeErrorHandler';
import ResourceStatusLegend from '../../components/ResourceStatusLegend';

const ResourceManagement = () => {
  const [expandedCategories, setExpandedCategories] = useState(['employees', 'equipment']);
  const [selectedResource, setSelectedResource] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalCategory, setAddModalCategory] = useState(null);
  const [editResource, setEditResource] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    status: 'all',
    usageTimeline: 'all'
  });
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [realtimeError, setRealtimeError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [bulkEditResources, setBulkEditResources] = useState(null);
  const [bulkDeleteResources, setBulkDeleteResources] = useState(null);
  const [bulkAssignResources, setBulkAssignResources] = useState(null);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  // Stat card drill-down modal
  const [statListModal, setStatListModal] = useState(null); // { title, resources }

  const categories = [
  { id: 'employees', name: 'Employees', icon: 'Users' },
  { id: 'contractors', name: 'Contractors', icon: 'UserCog' },
  { id: 'teams', name: 'Teams', icon: 'Users2' },
  { id: 'trucks', name: 'Trucks', icon: 'Truck' },
  { id: 'trailers', name: 'Trailers', icon: 'Container' },
  { id: 'equipment', name: 'Equipment', icon: 'Wrench' },
  { id: 'tools', name: 'Tools', icon: 'Hammer' },
  { id: 'other', name: 'Other', icon: 'Package' }];


  useEffect(() => {
    loadResources();

    const handleRealtimeError = (err) => {
      setRealtimeError(err);
      setIsRetrying(err?.willRetry || false);
      if (!err?.willRetry && err?.type !== RealtimeErrorType?.CONNECTION_FAILED) {
        setTimeout(() => setRealtimeError(null), 5000);
      }
    };

    const { cleanup: cleanupResources } = createRealtimeSubscription({
      supabase,
      channelName: 'resources_changes',
      tableName: 'resources',
      onDataChange: () => {
        loadResources();
        setRealtimeError(null);
        setIsRetrying(false);
      },
      onError: handleRealtimeError
    });

    const { cleanup: cleanupAssignments } = createRealtimeSubscription({
      supabase,
      channelName: 'resource_assignments_changes',
      tableName: 'resource_assignments',
      onDataChange: () => {
        loadResources();
        setRealtimeError(null);
        setIsRetrying(false);
      },
      onError: handleRealtimeError
    });

    return () => {
      cleanupResources();
      cleanupAssignments();
    };
  }, []);

  const loadResources = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await resourceService?.getAll();
      if (fetchError) throw fetchError;

      const today = new Date();
      today?.setHours(0, 0, 0, 0);

      const transformedData = (data || [])?.map((resource) => {
        const enrichedAssignments = (resource?.assignments || [])?.map((a) => {
          if (!a?.startDate || !a?.endDate) return a;
          const start = new Date(a.startDate);
          const end = new Date(a.endDate);
          start?.setHours(0, 0, 0, 0);
          end?.setHours(23, 59, 59, 999);
          return {
            ...a,
            isCurrent: start <= today && end >= today,
            isUpcoming: start > today
          };
        });

        return {
          ...resource,
          assignments: enrichedAssignments,
          categoryId: resource?.category,
          category: categories?.find((c) => c?.id === resource?.category)?.name || resource?.category,
          currentAssignment: enrichedAssignments?.find((a) => a?.isCurrent),
          upcomingAssignments: enrichedAssignments?.filter((a) => a?.isUpcoming) || [],
          conflicts: []
        };
      });

      setResources(transformedData);
    } catch (err) {
      console.error('Error loading resources:', err?.message);
      setError(err?.message || 'Failed to load resources');
      setResources([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateResource = async (resourceData) => {
    try {
      if (editResource) {
        const { data, error: updateError } = await resourceService?.update(editResource?.id, resourceData);
        if (updateError) throw updateError;
      } else {
        const { data, error: createError } = await resourceService?.create(resourceData);
        if (createError) throw createError;
      }
      await loadResources();
      setShowAddModal(false);
      setAddModalCategory(null);
      setEditResource(null);
    } catch (err) {
      console.error('Error saving resource:', err?.message);
      throw err;
    }
  };

  const handleUpdateResource = async (id, resourceData) => {
    try {
      const { data, error: updateError } = await resourceService?.update(id, resourceData);
      if (updateError) throw updateError;
      await loadResources();
    } catch (err) {
      console.error('Error updating resource:', err?.message);
      throw err;
    }
  };

  const handleSetMaintenanceStatus = async (id, updates) => {
    try {
      const { error: updateError } = await resourceService?.update(id, updates);
      if (updateError) throw updateError;
      await loadResources();
      // Refresh selected resource if open
      setSelectedResource((prev) => prev?.id === id ? { ...prev, ...updates } : prev);
    } catch (err) {
      console.error('Error updating resource status:', err?.message);
    }
  };

  const handleDeleteResource = async (id) => {
    try {
      const { error: deleteError } = await resourceService?.delete(id);
      if (deleteError) throw deleteError;
      await loadResources();
      setSelectedResource(null);
    } catch (err) {
      console.error('Error deleting resource:', err?.message);
      throw err;
    }
  };

  const handleBulkEdit = (selectedResources) => {
    setBulkEditResources(selectedResources);
  };

  const handleBulkDelete = (selectedResources) => {
    setBulkDeleteResources(selectedResources);
  };

  const handleBulkAssign = (selectedResources) => {
    setBulkAssignResources(selectedResources);
  };

  const confirmBulkDelete = async () => {
    if (!bulkDeleteResources?.length) return;
    setIsBulkDeleting(true);
    try {
      await Promise.all(bulkDeleteResources?.map((r) => resourceService?.delete(r?.id)));
      await loadResources();
      setBulkDeleteResources(null);
    } catch (err) {
      console.error('Error bulk deleting resources:', err?.message);
      setError(err?.message || 'Failed to delete resources');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleBulkApply = async (updates) => {
    const ids = bulkEditResources?.map((r) => r?.id);
    await Promise.all(ids?.map((id) => resourceService?.update(id, updates)));
    await loadResources();
    setBulkEditResources(null);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategories((prev) =>
    prev?.includes(categoryId) ?
    prev?.filter((id) => id !== categoryId) :
    [...prev, categoryId]
    );
  };

  const handleAddResource = (categoryId) => {
    setAddModalCategory(categoryId);
    setEditResource(null);
    setShowAddModal(true);
  };

  const handleSaveResource = (resourceData) => {
    console.log('Saving resource:', resourceData);
    setShowAddModal(false);
    setAddModalCategory(null);
  };

  const handleEditResource = async (resource) => {
    setSelectedResource(null);
    setEditResource(resource);
    setAddModalCategory(resource?.categoryId);
    setShowAddModal(true);
  };

  const handleResourceClick = (resource) => {
    setSelectedResource(resource);
  };

  const handleFilterChange = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      category: 'all',
      status: 'all',
      usageTimeline: 'all'
    });
  };

  const filteredResources = useMemo(() => {
    let result = [...resources];

    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      result = result?.filter((resource) =>
      resource?.name?.toLowerCase()?.includes(searchLower) ||
      resource?.identifier?.toLowerCase()?.includes(searchLower)
      );
    }

    if (filters?.category !== 'all') {
      result = result?.filter((resource) => resource?.categoryId === filters?.category);
    }

    if (filters?.status !== 'all') {
      result = result?.filter((resource) => {
        const status = resource?.status?.toLowerCase()?.replace(/\s+/g, '-') || '';
        if (filters?.status === 'conflicts') {
          return resource?.conflicts && resource?.conflicts?.length > 0;
        }
        return status === filters?.status;
      });
    }

    return result;
  }, [resources, filters]);

  const resourcesByCategory = useMemo(() => {
    return categories?.reduce((acc, category) => {
      acc[category?.id] = filteredResources?.filter((r) => r?.categoryId === category?.id);
      return acc;
    }, {});
  }, [filteredResources, categories]);

  const totalResources = resources?.length;

  const assignedIn30 = useMemo(() => {
    return resources?.filter((r) => r?.status === 'Assigned');
  }, [resources]);

  const availableIn30 = useMemo(() => {
    return resources?.filter((r) => r?.status === 'Available');
  }, [resources]);

  const maintenanceResources = useMemo(() => {
    return resources?.filter((r) => r?.status === 'Maintenance');
  }, [resources]);

  const conflictResources = useMemo(() => {
    return resources?.filter((r) => r?.conflicts && r?.conflicts?.length > 0);
  }, [resources]);

  const statCards = [
  {
    key: 'total',
    label: 'Total Resources',
    count: totalResources,
    icon: 'Package',
    iconBg: 'bg-accent/10',
    iconColor: 'text-accent',
    list: resources
  },
  {
    key: 'assigned',
    label: 'Assigned (Next 30 Days)',
    count: assignedIn30?.length,
    icon: 'Briefcase',
    iconBg: 'bg-blue-500/10',
    iconColor: 'text-blue-500',
    list: assignedIn30
  },
  {
    key: 'available',
    label: 'Available (Next 30 Days)',
    count: availableIn30?.length,
    icon: 'CheckCircle2',
    iconBg: 'bg-green-500/10',
    iconColor: 'text-green-500',
    list: availableIn30
  },
  {
    key: 'maintenance',
    label: 'In Maintenance',
    count: maintenanceResources?.length,
    icon: 'Wrench',
    iconBg: 'bg-yellow-500/10',
    iconColor: 'text-yellow-500',
    list: maintenanceResources
  }];


  const tabs = [
  { id: 'list', label: 'List View', icon: 'List' },
  { id: 'calendar', label: 'Calendar View', icon: 'Calendar' },
  { id: 'gantt', label: 'Gantt Chart', icon: 'BarChart3' },
  { id: 'conflicts', label: 'Resource Conflicts', icon: 'AlertTriangle' }];


  return (
    <MainLayout>
      <div className="space-y-4">
        {/* Real-time Error Alert */}
        {realtimeError &&
        <div className={`rounded-lg border p-3 flex items-start gap-3 ${
        realtimeError?.type === RealtimeErrorType?.CONNECTION_FAILED ?
        'bg-red-50 border-red-200 text-red-800' : 'bg-yellow-50 border-yellow-200 text-yellow-800'}`
        }>
            <Icon
            name={isRetrying ? 'RefreshCw' : 'AlertTriangle'}
            size={20}
            className={isRetrying ? 'animate-spin' : ''} />

            <div className="flex-1">
              <p className="font-medium text-sm">{realtimeError?.message}</p>
              {realtimeError?.attemptCount &&
            <p className="text-xs mt-1 opacity-80">Retry attempt {realtimeError?.attemptCount}/3</p>
            }
            </div>
            {!isRetrying && realtimeError?.type === RealtimeErrorType?.CONNECTION_FAILED &&
          <button onClick={() => window.location?.reload()} className="text-sm font-medium underline hover:no-underline">
                Refresh Page
              </button>
          }
            {!isRetrying && realtimeError?.type !== RealtimeErrorType?.CONNECTION_FAILED &&
          <button onClick={() => setRealtimeError(null)} className="text-sm font-medium">
                <Icon name="X" size={16} />
              </button>
          }
          </div>
        }

        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-0.5 -mt-6">
              Resource Management
            </h1>
            <p className="text-sm text-muted-foreground">
              Manage and track all construction resources including personnel, equipment, and materials
            </p>
          </div>
          <div className="flex gap-3 shrink-0">
            <Button
              variant="default"
              size="sm"
              onClick={() => setShowAddModal(true)}
              iconName="Plus"
              iconPosition="left"
              iconSize={18}>

              Add Resource
            </Button>
          </div>
        </div>

        {/* Summary Stats */}
        {!loading &&
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {statCards?.map((card) =>
          <button
            key={card?.key}
            onClick={() => setStatListModal({ title: card?.label, list: card?.list })}
            className="bg-card rounded-lg border border-border p-3 md:p-4 flex items-center gap-3 hover:border-accent/50 hover:shadow-sm transition-all text-left group w-full">

                {/* Icon */}
                <div className={`w-10 h-10 rounded-lg ${card?.iconBg} flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform`}>
                  <Icon name={card?.icon} size={20} className={card?.iconColor} />
                </div>
                {/* Count + Label */}
                <div className="min-w-0 flex-1">
                  <p className="text-xl md:text-2xl font-bold text-foreground leading-none mb-0.5">
                    {card?.count}
                  </p>
                  <p className="text-xs text-muted-foreground leading-tight truncate">
                    {card?.label}
                  </p>
                </div>
              </button>
          )}
          </div>
        }

        {/* Tab Navigation */}
        <div className="border-b border-border">
          <div className="flex gap-1">
            {tabs?.map((tab) =>
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors relative ${
              activeTab === tab?.id ?
              'text-accent border-b-2 border-accent' : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'}`
              }>

                <Icon name={tab?.icon} size={16} />
                {tab?.label}
              </button>
            )}
          </div>
        </div>

        <ResourceFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          onReset={handleResetFilters}
          categories={categories}
          activeTab={activeTab} />


        {loading ?
        <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
              <p className="text-sm text-muted-foreground">Loading resources...</p>
            </div>
          </div> :
        error ?
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <Icon name="AlertCircle" size={48} className="text-red-500 mx-auto mb-3" />
            <p className="text-red-700">{error}</p>
          </div> :
        activeTab === 'calendar' ?
        <ResourceCalendarView
          resources={filteredResources}
          filters={filters}
          onClose={() => setActiveTab('list')} /> :

        activeTab === 'gantt' ?
        <ResourceGanttView
          resources={filteredResources}
          filters={filters} /> :

        activeTab === 'conflicts' ?
        <ResourceConflictTab /> :
        <ResourceListView
          resources={filteredResources}
          categories={categories}
          onResourceClick={handleResourceClick}
          onBulkEdit={handleBulkEdit}
          onBulkDelete={handleBulkDelete}
          onBulkAssign={handleBulkAssign}
          filters={filters}
          externalFilters={filters}
          onFilterChange={handleFilterChange} />

        }
      </div>

      {/* Stat Card Drill-down Modal */}
      {statListModal &&
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-base font-semibold text-foreground">{statListModal?.title}</h3>
              <button
              onClick={() => setStatListModal(null)}
              className="text-muted-foreground hover:text-foreground transition-colors">

                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-4">
              {statListModal?.list?.length === 0 ?
            <div className="text-center py-8 text-muted-foreground text-sm">
                  No resources in this category.
                </div> :

            <div className="space-y-2">
                  {statListModal?.list?.map((resource) =>
              <button
                key={resource?.id}
                onClick={() => {
                  setStatListModal(null);
                  setSelectedResource(resource);
                }}
                className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:border-accent/50 hover:bg-muted/30 transition-all text-left">

                      <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                        <Icon name="User" size={14} className="text-accent" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">{resource?.name}</p>
                        <p className="text-xs text-muted-foreground truncate">
                          {resource?.category} {resource?.identifier ? `· ${resource?.identifier}` : ''}
                        </p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 ${
                resource?.status === 'Assigned' ? 'bg-blue-100 text-blue-700' :
                resource?.status === 'Available' ? 'bg-green-100 text-green-700' :
                resource?.status === 'Maintenance' ? 'bg-yellow-100 text-yellow-700' : 'bg-muted text-muted-foreground'}`
                }>
                        {resource?.status || 'Unknown'}
                      </span>
                    </button>
              )}
                </div>
            }
            </div>
            <div className="p-3 border-t border-border text-xs text-muted-foreground text-right">
              {statListModal?.list?.length} resource{statListModal?.list?.length !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      }

      {selectedResource &&
      <ResourceDetailModal
        resource={selectedResource}
        onClose={() => setSelectedResource(null)}
        onEdit={handleEditResource}
        onDelete={handleDeleteResource}
        onUpdateStatus={handleSetMaintenanceStatus} />

      }
      {showAddModal &&
      <AddResourceModal
        categoryId={addModalCategory}
        categories={categories}
        onClose={() => {
          setShowAddModal(false);
          setAddModalCategory(null);
          setEditResource(null);
        }}
        onSave={handleCreateResource}
        editResource={editResource} />

      }
      {bulkEditResources &&
      <BulkEditModal
        selectedCount={bulkEditResources?.length}
        categories={categories}
        onClose={() => setBulkEditResources(null)}
        onApply={handleBulkApply} />

      }
      {bulkAssignResources &&
      <BulkAssignModal
        selectedResources={bulkAssignResources}
        onClose={() => setBulkAssignResources(null)}
        onSuccess={() => {
          setBulkAssignResources(null);
          loadResources();
        }} />

      }
      {bulkDeleteResources &&
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
                  <Icon name="AlertTriangle" size={24} className="text-destructive" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">
                    Delete {bulkDeleteResources?.length} Resource{bulkDeleteResources?.length !== 1 ? 's' : ''}
                  </h3>
                  <p className="text-sm text-muted-foreground">This action cannot be undone</p>
                </div>
              </div>
              <p className="text-sm text-foreground mb-6">
                Are you sure you want to delete{' '}
                <span className="font-semibold">
                  {bulkDeleteResources?.length} resource{bulkDeleteResources?.length !== 1 ? 's' : ''}
                </span>? This will permanently remove them and all associated data.
              </p>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setBulkDeleteResources(null)} disabled={isBulkDeleting} className="flex-1">
                  Cancel
                </Button>
                <Button variant="destructive" onClick={confirmBulkDelete} disabled={isBulkDeleting} className="flex-1">
                  {isBulkDeleting ? 'Deleting...' : `Delete ${bulkDeleteResources?.length}`}
                </Button>
              </div>
            </div>
          </div>
        </div>
      }

      {/* Status Legend */}
      <ResourceStatusLegend />
    </MainLayout>);

};

export default ResourceManagement;